/**
 * CloudWatch RUM bootstrap — page views (SPA route changes included),
 * uncaught JS errors, and http telemetry, plus explicit signup-funnel
 * events. Bundled via npm (`aws-rum-web`), so no third-party <script> tag:
 * events flow to the AWS RUM dataplane, nothing to an ad-tech origin.
 *
 * Privacy posture (mirrors the site's consent-by-use model, see
 * docs/privacy-consent.md and the Termly CMP adapter):
 *  - A Global Privacy Control signal disables RUM entirely.
 *  - When the Termly CMP is PRESENT and the visitor has denied analytics,
 *    RUM stays off. (On routes without Termly, `readConsentPurposes`
 *    deliberately degrades to deny-all — that absence is NOT treated as a
 *    denial, or RUM would never run; see cmp.ts's header for the load
 *    scope.)
 *  - No RUM in local dev unless VITE_RUM_* vars point at a monitor.
 *
 * Best-effort by contract: init failures log a console.warn and leave the
 * app untouched — telemetry must never break the product.
 */

import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import { isTermlyScriptPresent, readConsentPurposes, readGpc } from '../consent/cmp.ts';
import { rumConfig } from '../config/rum-config.ts';

let client: AwsRum | null = null;

/** The version stamped on every event; ties telemetry to a deploy. */
const APP_VERSION = '1.0.0';

/**
 * URLs whose *query strings* must never reach the RUM dataplane.
 *
 * RUM's http telemetry records the FULL request URL, query string included, and
 * those records land in a CloudWatch log group the daily failure scan reads and
 * can email onward in a digest. On 2026-09-06 the prod vended group was found to
 * contain `api.oculusmind.ai/auth/status?token=<uuid>` — the magic-link sign-in
 * credential passed to `confirmSignIn` (see pages/auth/CheckEmail.tsx) — and
 * `agent-memory/<agent>?sessionId=<id>`.
 *
 * The first two entries are `aws-rum-web`'s own defaults, restated because
 * supplying `urlsToExclude` REPLACES the default list rather than extending it;
 * dropping them would start recording Cognito and STS calls.
 *
 * This stops the capture. It is not the whole fix: a credential in a query
 * string also leaks to proxies, referrers and browser history, so `/auth/status`
 * should take its token in a header or POST body. Tracked separately.
 *
 * Deliberately NOT excluded: `/agents`, `/guest/start` and the workflow/image
 * poll endpoints. They carry no secrets in the URL and their non-2xx responses
 * are exactly the signal we want.
 */
const HTTP_URLS_TO_EXCLUDE: readonly RegExp[] = [
  /cognito-identity\.([^.]*\.)?amazonaws\.com/,
  /sts\.([^.]*\.)?amazonaws\.com/,
  /\/auth\/status/,
  /\/agent-memory\//,
];

/**
 * Per-session event ceiling.
 *
 * The SDK default is 200 (`@aws-rum/web-slim`'s `defaultConfig`), and once a
 * session passes it every further `recordEvent` is dropped with only a console
 * warning. That default is actively hostile to failure telemetry: an active
 * session never rolls over (each recorded event renews it), and because
 * `recordAllRequests` is false only NON-OK responses count — so a browser stuck
 * in a failing 2.5s poll loop burns the whole budget in ~8 minutes and then
 * silently discards the stream/delivery failure events, which are precisely the
 * ones that explain what went wrong.
 *
 * 2000 is sized to outlast a long console session with a misbehaving poll while
 * staying far below anything that matters at $1/100k events (~37 turns/day in
 * prod as of 2026-09-06).
 */
const SESSION_EVENT_LIMIT = 2000;

/** Decide whether RUM may run for this visitor. Exported for tests. */
export async function rumAllowed(): Promise<boolean> {
  if (!rumConfig.appMonitorId || rumConfig.appMonitorId.startsWith('REPLACED_')) {
    return false;
  }
  if (readGpc()) {
    return false;
  }
  if (isTermlyScriptPresent()) {
    const purposes = await readConsentPurposes();
    return purposes.analytics;
  }
  return true;
}

/**
 * Initialise RUM once per page load. Safe to call unconditionally from the
 * app entry; resolves quickly and never throws.
 */
export async function initRum(): Promise<void> {
  if (client !== null) {
    return;
  }
  try {
    if (!(await rumAllowed())) {
      return;
    }
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId: rumConfig.identityPoolId,
      guestRoleArn: rumConfig.guestRoleArn,
      endpoint: `https://dataplane.rum.${rumConfig.region}.amazonaws.com`,
      telemetries: ['errors', 'performance', ['http', { urlsToExclude: [...HTTP_URLS_TO_EXCLUDE] }]],
      allowCookies: true,
      enableXRay: false,
      sessionEventLimit: SESSION_EVENT_LIMIT,
    };
    client = new AwsRum(rumConfig.appMonitorId, APP_VERSION, rumConfig.region, config);
  } catch (err) {
    console.warn('[rum] init failed (telemetry disabled)', err);
    client = null;
  }
}

/**
 * Record a signup failure the browser HANDLED (Cognito rejection, reCAPTCHA
 * mint failure, network error) — these never surface as uncaught errors, so
 * without an explicit event the funnel's last step is invisible. PII-free:
 * reason/kind only, never the email or username.
 */
export function recordSignupFailure(reason: string, detail?: string): void {
  try {
    client?.recordEvent('om_signup_failure', {
      reason,
      ...(detail !== undefined ? { detail: detail.slice(0, 200) } : {}),
    });
  } catch {
    // Telemetry must never perturb the flow it observes.
  }
}

/**
 * Record a failure the browser HANDLED and recovered from — a caught
 * exception on a retry loop, a poll that could not issue its request, any
 * path whose `catch` returns rather than throws.
 *
 * These are the failures nothing else can see. An uncaught error reaches RUM
 * on its own; a failed HTTP call reaches RUM's http telemetry and the
 * server's logs. A caught exception that stops the request from ever being
 * *made* reaches none of the three, and the screen keeps showing a spinner.
 *
 * Callers must record at most once per occurrence — never once per tick of a
 * retry loop, which would turn one stuck browser into a telemetry flood.
 *
 * PII-free: reason/kind only, never an email, username, or token.
 */
export function recordClientFailure(reason: string, detail?: string): void {
  try {
    client?.recordEvent('om_client_failure', {
      reason,
      ...(detail !== undefined ? { detail: detail.slice(0, 200) } : {}),
    });
  } catch {
    // Telemetry must never perturb the flow it observes.
  }
}

/**
 * Record an arbitrary custom event.
 *
 * The escape hatch for telemetry that needs more structure than
 * `reason`/`detail`, without dragging a feature's domain types into this
 * module: the payload is deliberately primitives-only, so the flattening (and
 * the decision about what is safe to send) stays with the code that owns the
 * concept. See `components/console/workflowDelivery.ts` for the pattern.
 *
 * PII rule is unchanged and non-negotiable: ids, counts, enums and booleans —
 * never message text, an email, a username, or a token.
 */
export function recordEvent(
  name: string,
  payload: Readonly<Record<string, string | number | boolean>>,
): void {
  try {
    client?.recordEvent(name, payload);
  } catch {
    // Telemetry must never perturb the flow it observes.
  }
}

/** Test seam: reset module state between specs. */
export function resetRumForTests(): void {
  client = null;
}

/** Test seam: inject a fake client. */
export function setRumClientForTests(fake: unknown): void {
  client = fake as AwsRum;
}
